import type { ReactNode } from 'react'
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react'
import type { UseQueryResult } from '@tanstack/react-query'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import type { AllowedChoice, ChipTone } from '@/lib/labelPrinting'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ─── List parts ───────────────────────────────────────────────────────────────

const CHIP_TONE: Record<ChipTone, string> = {
  ok: 'border-blue-200 bg-blue-50 text-blue-800',
  warn: 'border-amber-200 bg-amber-50 text-amber-800',
  off: 'border-border bg-muted text-muted-foreground',
}

export function StatusChip({ label, tone }: { label: string; tone: ChipTone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        CHIP_TONE[tone],
      )}
    >
      {label}
    </span>
  )
}

/**
 * A success or information line in the panel or a dialog; no toast library
 * here. An error is announced at once (role="alert"), the rest politely.
 */
export function StatusBanner({ tone, children }: { tone: 'ok' | 'info' | 'error'; children: ReactNode }) {
  const Icon = tone === 'ok' ? CheckCircle2 : tone === 'error' ? AlertCircle : Info
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
        tone === 'ok' && 'border-green-200 bg-green-50 text-green-800',
        tone === 'info' && 'border-blue-200 bg-blue-50 text-blue-800',
        tone === 'error' && 'border-destructive/30 bg-destructive/5 text-destructive',
      )}
    >
      <Icon className='mt-0.5 h-4 w-4 shrink-0' />
      <span>{children}</span>
    </div>
  )
}

/**
 * One list: title, a short explanation, the header controls, then the table,
 * or its loading, error or empty state.
 */
export function ListSection<T>({
  title,
  blurb,
  aside,
  footnote,
  query,
  noun,
  children,
}: {
  title: string
  blurb: string
  aside?: ReactNode
  footnote?: string
  query: UseQueryResult<T[], Error>
  /** For the empty and error lines, e.g. "templates". */
  noun: string
  children: (rows: T[]) => ReactNode
}) {
  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0 max-w-3xl'>
          <h3 className='text-base font-semibold'>{title}</h3>
          <p className='text-sm text-muted-foreground'>{blurb}</p>
        </div>
        {aside && <div className='flex shrink-0 items-center gap-2'>{aside}</div>}
      </div>
      <Card>
        <CardContent className='p-0'>
          {query.isLoading && (
            <div className='flex items-center justify-center py-16'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}
          {query.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load {noun}. {query.error.message}
            </div>
          )}
          {query.data && query.data.length === 0 && (
            <div className='py-12 text-center text-sm text-muted-foreground'>No {noun} yet.</div>
          )}
          {query.data && query.data.length > 0 && children(query.data)}
        </CardContent>
      </Card>
      {footnote && <p className='text-xs text-muted-foreground'>{footnote}</p>}
    </section>
  )
}

/** The shared table body: per-user columns and header sorting, as every table here has. */
export function ListTable<T extends { id: string }>({
  tableId,
  columns,
  sort,
  rows,
  cells,
}: {
  tableId: string
  columns: TableColumnDef[]
  sort: SortAccessors<T>
  rows: T[]
  cells: (row: T) => Record<string, ReactNode>
}) {
  const cols = useTableColumns(tableId, columns)
  const sorted = useSortedRows(rows, cols.sort, sort)
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {cols.visible.map(({ def }) => (
            <ResizableTableHead key={def.id} columnId={def.id} columns={cols} label={def.header}>
              {def.header}
            </ResizableTableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map(row => (
          <TableRow key={row.id}>
            <ColumnCells columns={cols} cells={cells(row)} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** The row's name: opens the dialog when there is one to open, plain text otherwise. */
export function NameCell({ name, onOpen }: { name: string; onOpen?: () => void }) {
  if (!onOpen) return <span className='font-medium'>{name}</span>
  return (
    <button type='button' onClick={onOpen} className='text-left font-medium text-primary hover:underline'>
      {name}
    </button>
  )
}

// ─── Form parts ───────────────────────────────────────────────────────────────

/** A labelled field with optional help text and error, ids wired for screen readers. */
export function Field({
  id,
  label,
  help,
  error,
  aside,
  className,
  children,
}: {
  id: string
  label: string
  help?: ReactNode
  error?: string
  /** Right of the label, e.g. a character count. */
  aside?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-1 flex-col gap-1.5', className)}>
      <div className='flex items-baseline justify-between gap-2'>
        <Label htmlFor={id}>{label}</Label>
        {aside}
      </div>
      {children}
      {help && <p className='text-xs text-muted-foreground'>{help}</p>}
      {error && (
        <p id={`${id}-error`} className='text-xs text-destructive'>
          {error}
        </p>
      )}
    </div>
  )
}

/** A switch with its label beside it and a note under it. */
export function SwitchField({
  id,
  label,
  checked,
  onCheckedChange,
  note,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  note?: string
}) {
  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center gap-3'>
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
        <Label htmlFor={id}>{label}</Label>
      </div>
      {note && <p className='text-xs text-muted-foreground'>{note}</p>}
    </div>
  )
}

export function signatureNote(required: boolean, withTestPrint: boolean): string {
  if (!required) return 'Labels print without a signature on this printer.'
  return `Every label printed here asks the user for an electronic signature before it is sent.${
    withTestPrint ? ' Test print never does.' : ''
  }`
}

export function activeNote(active: boolean): string | undefined {
  return active ? undefined : 'Retired: kept with its history, but not offered anymore.'
}

/**
 * A printer's "Allowed templates" checkboxes. A retired link shows checked and
 * cannot change: it stays on the printer, marked Retired.
 */
export function AllowedTemplatesField({
  legend,
  choices,
  selected,
  onChange,
  emptyText,
  note,
  error,
}: {
  legend: string
  choices: AllowedChoice[]
  selected: string[]
  onChange: (ids: string[]) => void
  emptyText: string
  note?: string | null
  error?: string
}) {
  const picked = new Set(selected)
  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...selected, id] : selected.filter(s => s !== id))
  return (
    <fieldset className='flex flex-col gap-2 rounded-lg border p-3' aria-describedby={error ? 'allowed-error' : undefined}>
      <legend className='px-1 text-sm font-medium'>{legend}</legend>
      {choices.map(c => {
        const id = `allowed-${c.id}`
        return (
          <div key={c.id} className='flex items-center gap-2'>
            <Checkbox
              id={id}
              checked={c.retired || picked.has(c.id)}
              disabled={c.retired}
              onCheckedChange={v => toggle(c.id, v === true)}
            />
            <Label htmlFor={id} className='font-normal'>
              {c.name}
              {c.retired && <span className='text-muted-foreground'> (Retired)</span>}
              {c.detail && <span className='ml-2 text-xs text-muted-foreground'>{c.detail}</span>}
            </Label>
          </div>
        )
      })}
      {choices.length === 0 && <p className='text-sm text-muted-foreground'>{emptyText}</p>}
      {note && <p className='text-xs text-muted-foreground'>{note}</p>}
      {error && (
        <p id='allowed-error' className='text-xs text-destructive'>
          {error}
        </p>
      )}
    </fieldset>
  )
}

/** Free text that suggests the values already used in this tenant. */
export function LocationInput({
  id,
  value,
  onChange,
  suggestions,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  suggestions: string[]
}) {
  return (
    <>
      <Input
        id={id}
        list={`${id}-suggestions`}
        value={value}
        autoComplete='off'
        onChange={e => onChange(e.target.value)}
      />
      <datalist id={`${id}-suggestions`}>
        {suggestions.map(s => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  )
}
